import type { TagChipVariant } from "@/types/tag-chip-variant";

export type ComplianceCategory = "allergen" | "dietary";

/** Allergen claim semantics — separates "contains" from positive free-from labels. */
export type ComplianceClaimKind = "contains" | "freeFrom";

export type ComplianceRegistryEntry = {
  category: ComplianceCategory;
  label: string;
  ariaLabel: string;
  icon?: string;
  variant: TagChipVariant;
  /** Allergen-only; defaults to contains when omitted. */
  claimKind?: ComplianceClaimKind;
};

/**
 * UI registry for allergen and dietary catalog tags.
 * Unknown codes fall back to API `tag_name` at runtime.
 * Codes align with EU-14 style taxonomy and RasoiX dietary tags.
 */
export const COMPLIANCE_REGISTRY: Record<string, ComplianceRegistryEntry> = {
  // — Dietary —
  VEG: {
    category: "dietary",
    label: "Veg",
    ariaLabel: "Vegetarian",
    variant: "subtle",
  },
  NON_VEG: {
    category: "dietary",
    label: "Non-veg",
    ariaLabel: "Non-vegetarian",
    variant: "subtle",
  },
  VEGAN: {
    category: "dietary",
    label: "Vegan",
    ariaLabel: "Vegan",
    icon: "🌱",
    variant: "filled",
  },
  VEGETARIAN: {
    category: "dietary",
    label: "Vegetarian",
    ariaLabel: "Vegetarian",
    variant: "subtle",
  },
  HALAL: {
    category: "dietary",
    label: "Halal",
    ariaLabel: "Halal certified",
    variant: "filled",
  },
  KOSHER: {
    category: "dietary",
    label: "Kosher",
    ariaLabel: "Kosher certified",
    variant: "filled",
  },
  PESCATARIAN: {
    category: "dietary",
    label: "Pescatarian",
    ariaLabel: "Pescatarian",
    variant: "subtle",
  },
  EGGITARIAN: {
    category: "dietary",
    label: "Eggitarian",
    ariaLabel: "Eggitarian",
    variant: "subtle",
  },

  // — EU-14 allergens (contains) —
  CELERY: {
    category: "allergen",
    label: "Celery",
    ariaLabel: "Contains celery",
    variant: "outline",
    claimKind: "contains",
  },
  GLUTEN: {
    category: "allergen",
    label: "Gluten",
    ariaLabel: "Contains gluten",
    icon: "🌾",
    variant: "outline",
    claimKind: "contains",
  },
  WHEAT: {
    category: "allergen",
    label: "Wheat",
    ariaLabel: "Contains wheat",
    icon: "🌾",
    variant: "outline",
    claimKind: "contains",
  },
  CRUSTACEANS: {
    category: "allergen",
    label: "Shellfish",
    ariaLabel: "Contains crustaceans",
    icon: "🦐",
    variant: "outline",
    claimKind: "contains",
  },
  EGGS: {
    category: "allergen",
    label: "Eggs",
    ariaLabel: "Contains eggs",
    icon: "🥚",
    variant: "outline",
    claimKind: "contains",
  },
  FISH: {
    category: "allergen",
    label: "Fish",
    ariaLabel: "Contains fish",
    icon: "🐟",
    variant: "outline",
    claimKind: "contains",
  },
  LUPIN: {
    category: "allergen",
    label: "Lupin",
    ariaLabel: "Contains lupin",
    variant: "outline",
    claimKind: "contains",
  },
  MILK: {
    category: "allergen",
    label: "Dairy",
    ariaLabel: "Contains milk or dairy",
    icon: "🥛",
    variant: "outline",
    claimKind: "contains",
  },
  DAIRY: {
    category: "allergen",
    label: "Dairy",
    ariaLabel: "Contains milk or dairy",
    icon: "🥛",
    variant: "outline",
    claimKind: "contains",
  },
  CONTAINS_DAIRY: {
    category: "allergen",
    label: "Dairy",
    ariaLabel: "Contains milk or dairy",
    icon: "🥛",
    variant: "outline",
    claimKind: "contains",
  },
  MOLLUSCS: {
    category: "allergen",
    label: "Molluscs",
    ariaLabel: "Contains molluscs",
    variant: "outline",
    claimKind: "contains",
  },
  MUSTARD: {
    category: "allergen",
    label: "Mustard",
    ariaLabel: "Contains mustard",
    variant: "outline",
    claimKind: "contains",
  },
  NUTS: {
    category: "allergen",
    label: "Nuts",
    ariaLabel: "Contains nuts",
    icon: "🥜",
    variant: "outline",
    claimKind: "contains",
  },
  TREE_NUTS: {
    category: "allergen",
    label: "Tree nuts",
    ariaLabel: "Contains tree nuts",
    icon: "🥜",
    variant: "outline",
    claimKind: "contains",
  },
  CONTAINS_NUTS: {
    category: "allergen",
    label: "Nuts",
    ariaLabel: "Contains nuts",
    icon: "🥜",
    variant: "outline",
    claimKind: "contains",
  },
  PEANUTS: {
    category: "allergen",
    label: "Peanuts",
    ariaLabel: "Contains peanuts",
    icon: "🥜",
    variant: "outline",
    claimKind: "contains",
  },
  SESAME: {
    category: "allergen",
    label: "Sesame",
    ariaLabel: "Contains sesame",
    variant: "outline",
    claimKind: "contains",
  },
  SOY: {
    category: "allergen",
    label: "Soy",
    ariaLabel: "Contains soy",
    variant: "outline",
    claimKind: "contains",
  },
  SOYA: {
    category: "allergen",
    label: "Soya",
    ariaLabel: "Contains soya",
    variant: "outline",
    claimKind: "contains",
  },
  SULPHITES: {
    category: "allergen",
    label: "Sulphites",
    ariaLabel: "Contains sulphites",
    variant: "outline",
    claimKind: "contains",
  },
  SULFITES: {
    category: "allergen",
    label: "Sulfites",
    ariaLabel: "Contains sulfites",
    variant: "outline",
    claimKind: "contains",
  },

  // — Positive / free-from claims (allergen type in taxonomy) —
  GLUTEN_FREE: {
    category: "allergen",
    label: "Gluten free",
    ariaLabel: "Gluten free",
    icon: "✓",
    variant: "filled",
    claimKind: "freeFrom",
  },
  DAIRY_FREE: {
    category: "allergen",
    label: "Dairy free",
    ariaLabel: "Dairy free",
    icon: "✓",
    variant: "filled",
    claimKind: "freeFrom",
  },
  NUT_FREE: {
    category: "allergen",
    label: "Nut free",
    ariaLabel: "Nut free",
    icon: "✓",
    variant: "filled",
    claimKind: "freeFrom",
  },
};
